import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';
import { catalogProductsOf, stockRowsOf } from '../../data/store';
import { CustomSelect } from '../../components/CustomSelect';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useSharedStore } from '../../hooks/useSharedStore';

export const WhInventoryStatusTab = ({ org, warehouseId }) => {
  const { version: dataVersion } = useSharedStore();
  const { isConnected } = useWebSocket(org.id);
  const products = useMemo(() => catalogProductsOf(org.id), [org.id, dataVersion]);
  const activeStock = useMemo(() => stockRowsOf(org.id, warehouseId, 'active'), [org.id, warehouseId, dataVersion]);
  const pendingStock = useMemo(() => stockRowsOf(org.id, warehouseId, 'pending'), [org.id, warehouseId, dataVersion]);

  const stockStatusList = useMemo(() => {
    const list = [];
    activeStock.forEach(row => {
      const minThreshold = Number(row.minStockThreshold) || 0;
      const numericQty = Number(row.qty) || 0;

      let status = 'In Stock';
      let statusType = 'success';

      if (numericQty === 0) {
        status = 'Out of Stock';
        statusType = 'error';
      } else if (minThreshold > 0 && numericQty <= minThreshold) {
        status = 'Low Stock';
        statusType = 'warning';
      } else {
        status = 'In Stock';
        statusType = 'success';
      }

      if (statusType !== 'success' && statusType !== 'info') {
        const product = products.find(p => p.id === row.productId);
        if (!product) return;
        const variant = product.variants?.find(v => v.id === row.variantId) || { id: '', label: 'Default' };

        // count pending qty for same product+variant
        const pendingQty = pendingStock
          .filter(p => p.productId === row.productId && p.variantId === row.variantId)
          .reduce((s, p) => s + (Number(p.qty) || 0), 0);

        list.push({
          id: row.id,
          productId: product.id,
          variantId: variant.id,
          name: product.name + (variant.label !== 'Default' ? ` - ${variant.label}` : ''),
          maxCount: minThreshold,
          activeQty: row.qty,
          pendingQty,
          status,
          statusType,
        });
      }
    });
    return list;
  }, [products, activeStock, pendingStock]);

  const [alertInterval, setAlertInterval] = useState(() => {
    return localStorage.getItem('mitra_wh_alert_interval') || '5';
  });

  const handleIntervalChange = (val) => {
    setAlertInterval(val);
    localStorage.setItem('mitra_wh_alert_interval', val);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col gap-6 w-full">
      <header className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-extrabold tracking-tight text-[#0d0d0d]">Stock Status</h2>
            {!isConnected && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-100">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500" aria-hidden="true" />
                <span className="text-[10px] font-extrabold uppercase tracking-wider">Offline</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-slate-400" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 hidden sm:inline">Alert Interval:</span>
            <div className="w-[110px]">
              <CustomSelect
                value={alertInterval}
                onChange={handleIntervalChange}
                options={[
                  { label: '1 Min', value: '1' },
                  { label: '5 Mins', value: '5' },
                  { label: '15 Mins', value: '15' },
                  { label: '30 Mins', value: '30' },
                  { label: '45 Mins', value: '45' },
                  { label: '60 Mins', value: '60' },
                ]}
              />
            </div>
          </div>
        </div>
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
          Monitor warehouse products that are running low on inventory
        </p>
      </header>

      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg shadow-sm flex flex-col overflow-hidden">
        {stockStatusList.length === 0 ? (
          <div className="py-12 text-center m-6 border border-dashed border-[var(--theme-tertiary)] rounded-lg">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">All warehouse products are well stocked.</p>
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
            <table className="w-full text-left text-[11px] whitespace-nowrap">
              <thead className="bg-[var(--theme-secondary)] border-b border-[var(--theme-tertiary)]">
                <tr>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400">Product Name</th>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400 text-center">Threshold (Min Count)</th>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400 text-center">Active Stock</th>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400 text-center">Pending Stock</th>
                  <th className="py-3 px-4 font-extrabold uppercase tracking-[0.16em] text-slate-400 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {stockStatusList.map((item) => (
                  <tr key={item.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 font-bold text-[#0d0d0d]">{item.name}</td>
                    <td className="py-3 px-4 text-slate-600 font-bold text-center">{item.maxCount || '—'}</td>
                    <td
                      className="py-3 px-4 font-bold text-center"
                      style={{ color: item.statusType === 'error' ? '#ef4444' : '#f59e0b' }}
                    >
                      {item.activeQty}
                    </td>
                    <td className="py-3 px-4 text-slate-600 font-bold text-center">{item.pendingQty}</td>
                    <td className="py-3 px-4 text-right">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                        item.statusType === 'error'
                          ? 'bg-rose-50 text-rose-600 border border-rose-100'
                          : 'bg-amber-50 text-amber-600 border border-amber-100'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${item.statusType === 'error' ? 'bg-rose-500' : 'bg-amber-500'}`} />
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </motion.div>
  );
};
